import {
  AttachmentIDType,
} from '@policyfly/protobuf'
import {
  AttachmentDataType,
  AttachmentDetail_ContentLocation_Field,
  AttachmentDetail_Download_Format,
  AttachmentVisibility,
  ArchiveAttachmentRequest,
  ArchiveAttachmentResponse,
  BulkRelocateAttachmentsRequest,
  BulkRelocateAttachmentsResponse,
  CreateAttachmentRequest,
  CreateAttachmentResponse,
  DeleteAttachmentRequest,
  DeleteAttachmentResponse,
  ListAttachmentsRequest,
  ListAttachmentsResponse,
  PatrickServiceClient,
  ReadAttachmentRequest,
  ReadAttachmentResponse,
  ReadLatestAttachmentByCategoryRequest,
  ReadLatestAttachmentByCategoryResponse,
  RelocateAttachmentRequest,
  RelocateAttachmentResponse,
  RenameAttachmentRequest,
  RenameAttachmentResponse,
  RestoreAttachmentRequest,
  RestoreAttachmentResponse,
  StreamingReadAttachmentRequest,
  StreamingReadAttachmentResponse,
  StreamingReadZipRequest,
  StreamingReadZipResponse,
  UpdateAttachmentVisibilityRequest,
  UpdateAttachmentVisibilityResponse,
} from '@policyfly/protobuf/patrick'
import { base64encode } from '@protobuf-ts/runtime'

import { getAccessToken } from '@/stores/api/interceptors'
import { useAttachmentStore } from '@/stores/attachment'
import { useAuthenticationStore } from '@/stores/authentication'
import { useProtobufStore } from '@/stores/protobuf'

import { api } from '@/api'
import { devtools } from '@/plugins/devtools/api'
import { httpResponseToRpcError } from '@/utils/api'
import { attachmentDownloadLocation } from '@/utils/attachments'
import {
  applicationAttachmentToAttachmentDetail,
  coreUserToUserDetail,
  stringToEnumAttachmentCategory,
} from '@/utils/protobuf'

import type { ApiVariableEndpoints, CreateEndpointParams } from '@/stores/api'
import type { AttachmentCategory } from '@policyfly/protobuf'
import type { AttachmentDetail, AttachmentDetail_ContentLocation } from '@policyfly/protobuf/patrick'
import type { AttachmentCategory as LegacyAttachmentCategory } from '@policyfly/schema/types/shared/attachmentCategory'
import type { RpcOptions, ServerStreamingCall } from '@protobuf-ts/runtime-rpc'
import type { ApplicationAttachment } from 'types/application'

export interface AttachmentApiEndpoints {
  /**
   * Archive an attachment, will return attachment data when soft archiving
   */
  archive: (id: string | number, soft: boolean) => Promise<AttachmentDetail | null>
  /**
   * Remove attachment metadata and delete stored data
   */
  delete: (id: string | number) => Promise<void>
  /**
   * List all attachments for currently loaded application or policy
   */
  list: (id: number, type?: AttachmentIDType, abortController?: AbortController) => Promise<AttachmentDetail[]>
  /**
   * Read attachment metadata from the server
   */
  read: (id: string | number) => Promise<AttachmentDetail>
  /**
   * Read the content an attachment holds in a field, to load a location pointing at that field
   */
  readContent: (id: string | number, field: AttachmentDetail_ContentLocation_Field) => Promise<AttachmentDetail_ContentLocation>
  /**
   * Retrieve the latest attachment matching the provided category
   */
  readCategory: (category: AttachmentCategory) => Promise<AttachmentDetail>
  /**
   * Bundle the requested attachments into a zip archive.
   */
  readZip: (attachmentIds: string[]) => Promise<AttachmentZip>
  /**
   * Change the group name of an attachment
   */
  relocate: (id: string | number, group: string) => Promise<AttachmentDetail>
  /**
   * Move multiple attachments to one group atomically. Empty group means the default group.
   */
  bulkRelocate: (attachmentIds: (string | number)[], group: string) => Promise<void>
  /**
   * Update the name of an attachment
   */
  rename: (id: string | number, name: string) => Promise<AttachmentDetail>
  /**
   * Unarchive an attachment that has been soft archived
   */
  restore: (id: string | number) => Promise<AttachmentDetail>
  /**
   * Set the visibility of an attachment
   */
  updateVisibility: (id: string | number, visibility: AttachmentVisibility) => Promise<AttachmentDetail>
  /**
   * Upload a file and store it on the server
   */
  upload: (
    file: File,
    meta: UploadMeta,
    fuzzy?: boolean,
  ) => Promise<AttachmentDetail>
}

/**
 * Metadata fields for uploading an attachment
 *
 * Both legacy and protobuf attachment categories are supported
 */
export interface UploadMeta extends Pick<CreateAttachmentRequest,
  | 'applicationId'
  | 'policyId'
  | 'policyDocument'
  | 'visibility'
  | 'group'
> {
  category?: AttachmentCategory | LegacyAttachmentCategory
}

/**
 * A zip archive of attachment data, joined from a streamed read.
 */
export interface AttachmentZip {
  /**
   * The zip archive containing the attachment data.
   */
  data: Uint8Array
  /**
   * Requested attachment ids that could not be included.
   */
  skippedIds: string[]
}

/**
 * One message of a streamed read, carrying a portion of the data.
 */
interface StreamedChunk {
  totalMessages: number
  messageIndex: number
  chunk: Uint8Array
}

/**
 * Reads every message of a streamed read, checking each arrives in order.
 *
 * Throws when the stream errors or ends without delivering every message it announced,
 * so a stream cut short never produces partial data. The call is cancelled on failure.
 */
async function readStream<T extends StreamedChunk> (
  startCall: (options: RpcOptions) => ServerStreamingCall<object, T>,
): Promise<T[]> {
  const abortController = new AbortController()
  const messages: T[] = []
  try {
    for await (const message of startCall({ abort: abortController.signal }).responses) {
      if (message.messageIndex !== messages.length) {
        throw new Error(`Expected stream message ${messages.length} but received ${message.messageIndex}`)
      }
      messages.push(message)
    }
    const totalMessages = messages[0]?.totalMessages ?? 0
    if (!messages.length || messages.length !== totalMessages) {
      throw new Error(`Stream ended after ${messages.length} of ${totalMessages} messages`)
    }
    return messages
  } catch (error) {
    abortController.abort()
    throw error
  }
}

/**
 * Joins the chunks of a streamed read into a single array of bytes.
 */
function joinChunks (messages: StreamedChunk[]): Uint8Array {
  const data = new Uint8Array(messages.reduce((size, { chunk }) => size + chunk.length, 0))
  let offset = 0
  for (const { chunk } of messages) {
    data.set(chunk, offset)
    offset += chunk.length
  }
  return data
}

/**
 * Creates endpoints for uploading and managing files
 */
export const createAttachmentEndpoints = (params: CreateEndpointParams): ApiVariableEndpoints<AttachmentApiEndpoints> => {
  const patrickServiceClient = new PatrickServiceClient(params.transport)

  const attachmentStore = useAttachmentStore()
  const authenticationStore = useAuthenticationStore()
  const protobufStore = useProtobufStore()

  /**
   * Unwraps an {@link AttachmentDetail} returned by the server.
   */
  function requireAttachmentDetail (attachment: AttachmentDetail | undefined, identifier: string | number): AttachmentDetail {
    if (!attachment) throw new Error(`Unable to retrieve Attachment: ${identifier}`)

    return attachment
  }

  /**
   * Converts a legacy Django attachment to an {@link AttachmentDetail}.
   */
  function djangoAttachmentToDetail (attachment: ApplicationAttachment | undefined, identifier: string | number): AttachmentDetail {
    const detail = attachment ? applicationAttachmentToAttachmentDetail(attachment) : undefined
    return requireAttachmentDetail(detail ?? undefined, identifier)
  }

  const archiveDjango: AttachmentApiEndpoints['archive'] = async (id, soft) => {
    const { data } = !soft
      ? await api.attachments.archive({ path: { pk: +id }, body: { log: false } })
      : await api.attachments.soft_archive({ path: { pk: +id } })

    // a permanently archived attachment can never be listed again, so none is returned
    if (!soft) return null

    return djangoAttachmentToDetail(data.Attachment as unknown as ApplicationAttachment, id)
  }
  const archiveGrpc: AttachmentApiEndpoints['archive'] = async (id, soft) => {
    const request = ArchiveAttachmentRequest.create({
      attachmentId: String(id),
      softArchive: soft,
    })
    const { response } = await patrickServiceClient.archiveAttachment(request)

    devtools.logGrpc({
      description: 'Archive Attachment',
      messages: [
        { type: ArchiveAttachmentRequest, key: 'request', message: request },
        { type: ArchiveAttachmentResponse, key: 'response', message: response },
      ],
    })

    // a permanently archived attachment can never be listed again, so none is returned
    if (!soft) return null

    return requireAttachmentDetail(response.attachment, id)
  }

  const deleteDjango: AttachmentApiEndpoints['delete'] = async (id) => {
    await api.attachments.remove({ path: { pk: +id } })
  }
  const deleteGrpc: AttachmentApiEndpoints['delete'] = async (id) => {
    const request: DeleteAttachmentRequest = { attachmentId: String(id) }
    const { response } = await patrickServiceClient.deleteAttachment(request)

    devtools.logGrpc({
      description: 'Delete Attachment',
      messages: [
        { type: DeleteAttachmentRequest, key: 'request', message: request },
        { type: DeleteAttachmentResponse, key: 'response', message: response },
      ],
    })
  }

  const listDjango: AttachmentApiEndpoints['list'] = async (_id, type) => {
    // django delivers attachments with the application rather than by listing them
    return type === AttachmentIDType.ID_Type_Policy
      ? attachmentStore.allPolicyAttachments
      : attachmentStore.allApplicationAttachments
  }

  const listGrpc: AttachmentApiEndpoints['list'] = async (id, type = AttachmentIDType.ID_Type_Application, abortController) => {
    const attachmentRequest = ListAttachmentsRequest.create({
      id: `${id}`,
      type,
    })
    const { response: attachmentResponse } = await patrickServiceClient.listAttachments(attachmentRequest, { abort: abortController?.signal })

    devtools.logGrpc({
      description: 'List Attachments',
      messages: [
        { type: ListAttachmentsRequest, key: 'request', message: attachmentRequest },
        { type: ListAttachmentsResponse, key: 'response', message: attachmentResponse },
      ],
    })

    return attachmentResponse.attachments
  }

  const readDjango: AttachmentApiEndpoints['read'] = async (id) => {
    const { data } = await api.attachments.read({ path: { pk: id } })

    return djangoAttachmentToDetail(data.Attachment as unknown as ApplicationAttachment, id)
  }

  const readGrpc: AttachmentApiEndpoints['read'] = async (id) => {
    const request = ReadAttachmentRequest.create({ attachmentId: String(id) })
    const { response } = await patrickServiceClient.readAttachment(request)

    devtools.logGrpc({
      description: 'Read Attachment',
      messages: [
        { type: ReadAttachmentRequest, key: 'request', message: request },
        { type: ReadAttachmentResponse, key: 'response', message: response },
      ],
    })

    return requireAttachmentDetail(response.attachment, id)
  }

  const readContentDjango: AttachmentApiEndpoints['readContent'] = async (id, field) => {
    // django has no fields to read, so the matching location of a full read is used instead
    const attachment = await readDjango(id)
    const location = field === AttachmentDetail_ContentLocation_Field.FIELD_PREVIEW
      ? attachment.preview?.location
      : attachmentDownloadLocation(attachment, AttachmentDetail_Download_Format.FORMAT_ORIGINAL)
    if (!location) throw new Error(`Unable to retrieve Attachment content: ${id}`)

    return location
  }
  const readContentGrpc: AttachmentApiEndpoints['readContent'] = async (id, field) => {
    const request = StreamingReadAttachmentRequest.create({
      attachmentId: String(id),
      dataType: field === AttachmentDetail_ContentLocation_Field.FIELD_PREVIEW
        ? AttachmentDataType.ATTACHMENT_DATA_TYPE_PREVIEW
        : AttachmentDataType.ATTACHMENT_DATA_TYPE_RAW,
    })
    const messages = await readStream((options) => patrickServiceClient.streamingReadAttachment(request, options))

    devtools.logGrpc({
      description: 'Read Attachment Content',
      messages: [
        { type: StreamingReadAttachmentRequest, key: 'request', message: request },
        { type: StreamingReadAttachmentResponse, key: 'last response', message: messages.at(-1) },
      ],
    })

    return { location: { oneofKind: 'bytes', bytes: joinChunks(messages) } }
  }

  const readCategoryDjango: AttachmentApiEndpoints['readCategory'] = async () => {
    throw new Error('attachment.readCategory endpoint is not supported on Django')
  }
  const readCategoryGrpc: AttachmentApiEndpoints['readCategory'] = async (category) => {
    const request = ReadLatestAttachmentByCategoryRequest.create({
      category,
      id: String(protobufStore.workingCopies?.policyState?.id),
    })
    const { response } = await patrickServiceClient.readLatestAttachmentByCategory(request)

    devtools.logGrpc({
      description: 'Read Attachment Category',
      messages: [
        { type: ReadLatestAttachmentByCategoryRequest, key: 'request', message: request },
        { type: ReadLatestAttachmentByCategoryResponse, key: 'response', message: response },
      ],
    })

    return requireAttachmentDetail(response.attachment, category)
  }

  const readZipDjango: AttachmentApiEndpoints['readZip'] = async () => {
    throw new Error('attachment.readZip endpoint is not supported on Django')
  }
  const readZipGrpc: AttachmentApiEndpoints['readZip'] = async (attachmentIds) => {
    const request = StreamingReadZipRequest.create({ attachmentIds })
    const messages = await readStream((options) => patrickServiceClient.streamingReadZip(request, options))

    devtools.logGrpc({
      description: 'Read Attachment Zip',
      messages: [
        { type: StreamingReadZipRequest, key: 'request', message: request },
        { type: StreamingReadZipResponse, key: 'last response', message: messages.at(-1) },
      ],
    })

    return {
      data: joinChunks(messages),
      // gathered from every message, as the stream does not say which ones will carry them
      skippedIds: [...new Set(messages.flatMap(({ skippedIds }) => skippedIds))],
    }
  }

  const relocateDjango: AttachmentApiEndpoints['relocate'] = async () => {
    throw new Error('attachment.relocate endpoint is not supported on Django')
  }
  const relocateGrpc: AttachmentApiEndpoints['relocate'] = async (id, group) => {
    const request = RelocateAttachmentRequest.create({ attachmentId: String(id), newGroup: group })
    const { response } = await patrickServiceClient.relocateAttachment(request)

    devtools.logGrpc({
      description: 'Relocate Attachment',
      messages: [
        { type: RelocateAttachmentRequest, key: 'request', message: request },
        { type: RelocateAttachmentResponse, key: 'response', message: response },
      ],
    })

    return requireAttachmentDetail(response.attachment, id)
  }

  const bulkRelocateDjango: AttachmentApiEndpoints['bulkRelocate'] = async () => {
    throw new Error('attachment.bulkRelocate endpoint is not supported on Django')
  }
  const bulkRelocateGrpc: AttachmentApiEndpoints['bulkRelocate'] = async (attachmentIds, group) => {
    const request = BulkRelocateAttachmentsRequest.create({ attachmentIds: attachmentIds.map(String), newGroup: group })
    const { response } = await patrickServiceClient.bulkRelocateAttachments(request)

    devtools.logGrpc({
      description: 'Bulk Relocate Attachments',
      messages: [
        { type: BulkRelocateAttachmentsRequest, key: 'request', message: request },
        { type: BulkRelocateAttachmentsResponse, key: 'response', message: response },
      ],
    })
  }

  const renameDjango: AttachmentApiEndpoints['rename'] = async (id, name) => {
    // django endpoint requires name without extension
    const { data } = await api.attachments.rename({ path: { pk: +id }, body: { name: name.split('.')[0]! } })

    return djangoAttachmentToDetail(data.Attachment as unknown as ApplicationAttachment, id)
  }
  const renameGrpc: AttachmentApiEndpoints['rename'] = async (id, name) => {
    const request = RenameAttachmentRequest.create({ attachmentId: String(id), newName: name })
    const { response } = await patrickServiceClient.renameAttachment(request)

    devtools.logGrpc({
      description: 'Rename Attachment',
      messages: [
        { type: RenameAttachmentRequest, key: 'request', message: request },
        { type: RenameAttachmentResponse, key: 'response', message: response },
      ],
    })

    return requireAttachmentDetail(response.attachment, id)
  }

  const restoreDjango: AttachmentApiEndpoints['restore'] = async (id) => {
    const { data } = await api.attachments.restore({ path: { pk: +id } })

    return djangoAttachmentToDetail(data.Attachment as unknown as ApplicationAttachment, id)
  }
  const restoreGrpc: AttachmentApiEndpoints['restore'] = async (id) => {
    const request: RestoreAttachmentRequest = { attachmentId: String(id) }
    const { response } = await patrickServiceClient.restoreAttachment(request)

    devtools.logGrpc({
      description: 'Restore Attachment',
      messages: [
        { type: RestoreAttachmentRequest, key: 'request', message: request },
        { type: RestoreAttachmentResponse, key: 'response', message: response },
      ],
    })

    return requireAttachmentDetail(response.attachment, id)
  }

  const uploadDjango: AttachmentApiEndpoints['upload'] = async (file, meta, fuzzy) => {
    const payload = {
      application: Number(meta.applicationId),
      policyId: undefined,
      policy: meta.policyDocument ? Number(meta.policyId) : undefined,
      category: meta.category,
      file,
    }
    const formData = new FormData()
    Object.entries(payload).forEach(([key, value]: [string, string | number | boolean | File | undefined]) => {
      if (value !== undefined) formData.append(key, key === 'file' ? value as File : String(value))
    })

    // Use policy upload endpoint when called from PolicyDocsPage
    const { data } = meta.policyDocument
      ? await api.policies.upload({ body: formData })
      : await api.applications.upload({ body: formData, query: fuzzy ? { mode: 'fuzzy' } : {} })

    // @ts-expect-error(external): attachment types on data are completely wrong
    const attachment = (meta.policyDocument ? data.Policy.attachments[0] : data.Attachment) as ApplicationAttachment
    return djangoAttachmentToDetail(attachment, file.name)
  }
  const uploadGrpc: AttachmentApiEndpoints['upload'] = async (file, meta) => {
    const request = CreateAttachmentRequest.create({
      name: file.name,
      mimeType: file.type,
      sizeBytes: String(file.size),
      applicationId: meta.applicationId,
      policyId: meta.policyId,
      policyDocument: meta.policyDocument,
      programId: String(authenticationStore.programIds.id),
      category: typeof meta.category === 'string'
        ? stringToEnumAttachmentCategory(meta.category)
        : meta.category,
      owner: coreUserToUserDetail(authenticationStore.coreUser),
      visibility: meta.visibility,
      group: meta.group,
    })
    // gRPC-Web cannot do client streaming, so the file is posted to an HTTP route that mimics it
    const httpResponse = await fetch('/patrick.v1.PatrickService/attachments', {
      method: 'POST',
      body: file,
      headers: {
        'attachment-request-bin': base64encode(CreateAttachmentRequest.toBinary(request)),
        authorization: await getAccessToken(params),
        slug: authenticationStore.slug || '',
      },
    })
    if (!httpResponse.ok) throw await httpResponseToRpcError(httpResponse)
    const response = CreateAttachmentResponse.fromBinary(new Uint8Array(await httpResponse.arrayBuffer()))

    devtools.logGrpc({
      description: 'Upload Attachment',
      messages: [
        { type: CreateAttachmentRequest, key: 'request', message: request },
        { type: CreateAttachmentResponse, key: 'response', message: response },
      ],
    })

    return requireAttachmentDetail(response.attachment, file.name)
  }

  const updateVisibilityDjango: AttachmentApiEndpoints['updateVisibility'] = async (id, visibility) => {
    const { data } = await api.attachments.set_visibility({
      path: { pk: +id },
      body: { restricted_visibility: visibility === AttachmentVisibility.ATTACHMENT_VISIBILITY_RESTRICTED },
    })

    return djangoAttachmentToDetail(data.Attachment as unknown as ApplicationAttachment, id)
  }
  const updateVisibilityGrpc: AttachmentApiEndpoints['updateVisibility'] = async (id, visibility) => {
    const request = UpdateAttachmentVisibilityRequest.create({
      attachmentId: String(id),
      newVisibility: visibility,
    })
    const { response } = await patrickServiceClient.updateAttachmentVisibility(request)

    devtools.logGrpc({
      description: 'Set Attachment Visibility',
      messages: [
        { type: UpdateAttachmentVisibilityRequest, key: 'request', message: request },
        { type: UpdateAttachmentVisibilityResponse, key: 'response', message: response },
      ],
    })

    return requireAttachmentDetail(response.attachment, id)
  }

  return {
    django: {
      archive: archiveDjango,
      delete: deleteDjango,
      list: listDjango,
      read: readDjango,
      readContent: readContentDjango,
      readCategory: readCategoryDjango,
      readZip: readZipDjango,
      relocate: relocateDjango,
      bulkRelocate: bulkRelocateDjango,
      rename: renameDjango,
      restore: restoreDjango,
      updateVisibility: updateVisibilityDjango,
      upload: uploadDjango,
    },
    grpc: {
      archive: archiveGrpc,
      delete: deleteGrpc,
      list: listGrpc,
      read: readGrpc,
      readContent: readContentGrpc,
      readCategory: readCategoryGrpc,
      readZip: readZipGrpc,
      relocate: relocateGrpc,
      bulkRelocate: bulkRelocateGrpc,
      rename: renameGrpc,
      restore: restoreGrpc,
      updateVisibility: updateVisibilityGrpc,
      upload: uploadGrpc,
    },
  }
}
